import { randomBytes, randomInt } from 'crypto';
import { M2HText } from 'helpers/constants/cart';
import { Variant } from 'types/entity/product';

export function secureNibble(): number {
  if (typeof randomInt === 'function') {
    return randomInt(0, 16);
  }
  return randomBytes(1)[0] & 0x0f;
}

export function roundToSix(num: number): number {
  return Math.round((num + Number.EPSILON) * 1000000) / 1000000;
}

/**
 * Checks for non-nullish, with type narrowing.
 */
export function isNonNullish<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
};

export function cleanString(input: string, limit: number = 20): string {
  if (!input) return '';
  let cleaned = input
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .join('');

  if (!cleaned) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const randomString = Array.from({ length: limit }, () => chars[Math.floor(secureNibble())]).join('');
    cleaned = randomString;
  }

  return cleaned.slice(0, limit);
}

export function buildProductCode(
  override: string | undefined,
  prefix: string | undefined,
  itemId: string | undefined,
): string {
  const productCodePrefix = override?.replaceAll(' ', '') ?? '';
  if (productCodePrefix) {
    return `${productCodePrefix}`;
  } else {
    return `${prefix};${itemId}`;
  }
}

export function normalizeCmsDate(dateStr: string) {
  const date = new Date(dateStr);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000).toISOString();
}

export function showBadge(badgeAvailableFrom?: string, badgeExpiresOn?: string): boolean {
  if (!badgeAvailableFrom || !badgeExpiresOn) {
    return false;
  }

  const now = Date.now();
  const availableFrom = Date.parse(badgeAvailableFrom);
  const expiresOn = Date.parse(badgeExpiresOn);

  return (
    !Number.isNaN(availableFrom) &&
    !Number.isNaN(expiresOn) &&
    availableFrom <= expiresOn &&
    now >= availableFrom &&
    now <= expiresOn
  );
}
type LineItem = {
  lineItemId?: string;
  distributionChannel?: { key?: string | null } | null;
};

type CartItem = {
  quantity?: number;
  lineItemId?: string;
};

type RemovePayload = {
  cartItems: CartItem[];
  distributionChannelKey: string;
};

export function buildRemovePayload(item: LineItem): RemovePayload;
export function buildRemovePayload(items: LineItem[]): RemovePayload;

export function buildRemovePayload(item: LineItem | LineItem[]): RemovePayload {
  if (Array.isArray(item)) {
    const cartItems = item.map((lineItem) => ({
      quantity: 0,
      lineItemId: lineItem?.lineItemId as string,
    }));

    return {
      cartItems,
      distributionChannelKey: item?.[0]?.distributionChannel?.key ?? '',
    };
  }

  const lineItem = item;

  return {
    cartItems: [
      {
        quantity: 0,
        lineItemId: lineItem?.lineItemId as string,
      },
    ],
    distributionChannelKey: lineItem?.distributionChannel?.key ?? '',
  };
}

// Checking for Product is Out of Stock
export function isProductAvailableInStore(codes: unknown, attrs: any) {
  if (!Array.isArray(codes)) return false;

  const overrideCode = attrs?.product_code_override_universal?.trim();
  if (overrideCode) {
    return codes.includes(overrideCode);
  }

  const prefix = attrs?.product_code_prefix?.trim();
  const itemId = attrs?.product_item_id_universal?.trim();

  if (prefix && itemId) {
    return codes.includes(`${prefix};${itemId}`);
  }

  return false;
}

export const hasDiscount = (variant: Variant): boolean => {
  // Checks the resolved price for the currently selected channel/country.
  if (variant?.discountedPrice?.value?.centAmount != null) {
    return true;
  }
  
  // Fallback: checks every price configured for the variant.
  return (
    variant?.prices?.some(
      price => price?.discounted?.value?.centAmount != null
    ) ?? false
  );
};


export const toKebabCase = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

export const isEURegion = (storeKey?: string): boolean => {
  const normalizedStoreKey = storeKey?.toLowerCase();

  return normalizedStoreKey !== 'region-us' && normalizedStoreKey !== 'region-ca';
};   

export const calculateArea = (value: string): number => {
    const [length, width] = String(value).toLowerCase().split('x').map(Number);

    return (length || 0) * (width || 0);
  };

// Sorts a copy of items ascending by print area (e.g. "8x10" -> 80), derived via getSizeLabel.
export const sortByArea = <T>(items: T[], getSizeLabel: (item: T) => string | undefined): T[] =>
  [...items].sort((a, b) => calculateArea(getSizeLabel(a) ?? '') - calculateArea(getSizeLabel(b) ?? ''));

export const getMasterDeliveryChannel = (variant?: Variant): string | undefined => variant?.attributes?.masterDeliveryChannel?.key?.toUpperCase();

export const getDefaultIsP2rSelected = (variant?: Variant): boolean => {
  const masterChannel = getMasterDeliveryChannel(variant)?.toLowerCase();
  return masterChannel !== M2HText;
};

export const isValidForCurrentTime = (validFrom?: string, validTo?: string): boolean => {
  const nowTime = Date.now();

  if (validFrom) {
    const from = Date.parse(validFrom);
    if (!Number.isNaN(from) && nowTime < from) return false;
  }

  if (validTo) {
    const to = Date.parse(validTo);
    if (!Number.isNaN(to) && nowTime > to) return false;
  }

  return true;
};